import type { UserFormData } from 'shared/interfaces/users.interface';

import { Button } from 'components/buttons/button/button';
import { InputField } from 'components/fields/input-field/input-field';
import { SelectField } from 'components/fields/select-field/select-field';
import { GrayPanel } from 'components/panels/gray-panel/gray-panel';
import { Container } from 'components/template/container/container';
import { FormHeader } from 'components/template/form-header/FormHeader';
import { PermissionsContext } from 'contexts';
import { Form, Formik } from 'formik';
import { useContext } from 'react';
import { MODAL_USER } from 'shared/constants/modals';
import { validateField } from 'shared/utils';
import CheckIfFormIsDirty from 'shared/utils/checkIfFormIsDirty';
import { useAppDispatch, useAppSelector } from 'store';
import { closeModal } from 'store/modals/modals.slice';
import { inviteUserThunk } from 'store/users/users.thunks';
import { vocabulary } from 'vocabulary/german';

interface UserInvitationFormProps {
  page: number;
}

export const UserInvitationForm = ({ page }: UserInvitationFormProps) => {
  const dispatch = useAppDispatch();
  const { permissions } = useContext(PermissionsContext);
  const currentYearFrozen = useAppSelector(
    (state) => state.years.currentYearFrozen,
  );
  const users = useAppSelector((state) => state.users);

  const initialValues: UserFormData = {
    id: null,
    username: '',
    email: '',
    role: 'viewer',
  };

  if (users.current) {
    initialValues.id = users.current.id ? users.current.id : null;
    initialValues.username = users.current.username
      ? users.current.username
      : '';
    initialValues.email = users.current.email ? users.current.email : '';
  }

  const handleSave = (user: UserFormData) => {
    dispatch(inviteUserThunk({ ...user, roles: [user.role], page }));
    dispatch(closeModal({ name: MODAL_USER }));
  };

  const renderSaveButton = () => {
    if (permissions.inviteNewUsers === true && !currentYearFrozen) {
      return <Button type={'submit'} text={vocabulary.buttons.save} />;
    }
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={(values) => {
        handleSave(values);
      }}
    >
      {({ errors, touched, dirty, ...rest }) => {
        return (
          <Form>
            <CheckIfFormIsDirty dirty={dirty} />
            <FormHeader renderSave={renderSaveButton()} />
            <GrayPanel text={vocabulary.forms.users.description} people />

            <Container>
              <div className="field-wrapper">
                <InputField
                  name="username"
                  placeholder={vocabulary.forms.users.placeholders.username}
                  validate={validateField}
                  className={'form__field_big'}
                />
                {errors.username && touched.username && (
                  <p className={'field-error'}>{errors.username}</p>
                )}
              </div>
              <div className="field-wrapper">
                <InputField
                  name="email"
                  placeholder={vocabulary.forms.users.placeholders.email}
                  validate={validateField}
                  className={'form__field_big'}
                />
                {errors.email && touched.email && (
                  <p className={'field-error'}>{errors.email}</p>
                )}
              </div>
              <p>{vocabulary.forms.users.placeholders.role}</p>
              <div className="field-wrapper">
                <SelectField
                  name="role"
                  className=""
                  value={rest?.values?.role}
                >
                  <option value="viewer" label={vocabulary.roles.viewer} />
                  <option value="manager" label={vocabulary.roles.manager} />
                  <option value="admin" label={vocabulary.roles.admin} />
                </SelectField>
              </div>
            </Container>
          </Form>
        );
      }}
    </Formik>
  );
};
